import { Button, Card, Checkbox, Typography } from '@sonnen/web-ui';
import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { useAppContext } from '../../AppContext/AppContext';
import AddressSection from '../../components/AddressSection/AddressSection';
import { DataCard } from '../../components/DataCard/DataCard';
import { OrderFlowPage } from '../../components/OrderFlowPage/OrderFlowPage';
import PersonalDataSection from '../../components/PersonalDataSection/PersonalDataSection';
import { ACCOUNT_OWNER_MAX_LENGTH, DataSection, sectionsByFlow } from '../../constants';
import {
  CurrentUserAccountBillingAddress,
  CurrentUserSiteOptionsCreateContractFlow,
  ProviderChangeReason,
} from '../../graphql/generated';
import { useOrderFlowNavigation } from '../../hooks/useOrderFlowNavigation';
import { useTopScrollNavigate } from '../../hooks/useTopScrollNavigate';
import { TValidationSchema, useValidation } from '../../hooks/useValidation';
import { useTranslation } from '../../i18n/i18n';
import { OrderFlow, TAddress, TFormValues, TPersonalData } from '../../types';
import {
  businessAccount,
  completeAddress,
  getFullName,
  getSwitchFromExistingContract,
  getValidDeliveryAddress,
} from '../../utils/utils';
import {
  accountOwnerValidator,
  addressValidator,
  ibanValidator,
  meterValidator,
  personalDataValidator,
  validateExtendedPersonalData,
} from '../../utils/validation';
import commonStyles from '../Common.module.css';
import { Checkboxes } from './Checkboxes/CheckBoxes';
import styles from './DataEntry.module.css';
import { DeliveryDetails } from './DeliveryDetails/DeliveryDetails';
import { PaymentDetails } from './PaymentDetails/PaymentDetails';

const DataEntry = () => {
  const navigate = useTopScrollNavigate();
  const { t } = useTranslation();
  const {
    user,
    formValues,
    accountType,
    setFormValues,
    selectedSite,
    offerRequiresManualAddress,
    customBillingAddress,
    setCustomBillingAddress,
    orderFlow,
  } = useAppContext();

  const getValidAccountOwner = (value?: string | null): boolean => {
    if (!value || value.trim().length === 0) return false;
    if (value.length > ACCOUNT_OWNER_MAX_LENGTH) return false;
    return true;
  };

  const [localValues, setLocalValues] = useState<TFormValues>(() => {
    const initialValues = {
      providerChangeReason: undefined,
      ...formValues,
    };

    if (initialValues.accountOwner && !getValidAccountOwner(initialValues.accountOwner)) {
      return {
        ...initialValues,
        accountOwner: '',
      };
    }

    return initialValues;
  });

  const { next: nextRoute } = useOrderFlowNavigation();

  const switchFromExistingContract = getSwitchFromExistingContract(selectedSite);
  const customerAddress = getValidDeliveryAddress(user, selectedSite);
  const customerWithCompleteAddress = addressValidator(customerAddress);
  const customerWithCompletePersonalData = personalDataValidator(user?.contact);
  const customerWithCompleteBillingAddress =
    user && addressValidator(completeAddress(user.account.billingAddress));
  const tariffFlow = [OrderFlow.energyDynamic, OrderFlow.energyFix].includes(orderFlow!);

  const readonlyFields: Set<keyof TAddress> = new Set();
  if (tariffFlow) readonlyFields.add('postalCode');
  const readonlyFieldsBilling: Set<keyof TAddress> = new Set();

  if (offerRequiresManualAddress) readonlyFields.add('city');

  if (!customerWithCompleteAddress) {
    if (customerAddress.city) readonlyFields.add('city');
    if (customerAddress.streetName) readonlyFields.add('streetName');
    if (customerAddress.streetNumber) readonlyFields.add('streetNumber');
  }

  if (user && !customerWithCompleteBillingAddress) {
    if (user.account.billingAddress.postalCode) readonlyFieldsBilling.add('postalCode');
    if (user.account.billingAddress.city) readonlyFieldsBilling.add('city');
    if (user.account.billingAddress.streetName) readonlyFieldsBilling.add('streetName');
    if (user.account.billingAddress.streetNumber) readonlyFieldsBilling.add('streetNumber');
  }

  const showAddressSection =
    (!user && customBillingAddress) || (user && !customerWithCompleteBillingAddress);

  const validatePersonalData = (personalData: TPersonalData | undefined) => {
    return validateExtendedPersonalData(personalData, Boolean(user), businessAccount(accountType));
  };

  const requireCancellationAndStartDates =
    localValues.contractTerminated &&
    (Boolean(user) || localValues.providerChangeReason === ProviderChangeReason.Relocation);

  const preFilledAccountOwner = formValues.accountOwner;

  const getAccountOwnerErrorMessage = () => {
    const fullName = getFullName(localValues.personalData);
    if (
      preFilledAccountOwner &&
      !getValidAccountOwner(preFilledAccountOwner) &&
      !localValues.accountOwner
    ) {
      return t('data.paymentDetails.accountOwnerTooLongError');
    }
    if (fullName && !getValidAccountOwner(fullName) && !localValues.accountOwner) {
      return t('data.paymentDetails.accountOwnerTooLongError');
    }
    return t('data.paymentDetails.accountOwnerError');
  };

  const schema: TValidationSchema<TFormValues> = useMemo(
    () => ({
      personalData: {
        required: true,
        validator: validatePersonalData,
        message: t('data.personalDataError'),
      },
      deliveryAddress: {
        required: !switchFromExistingContract && tariffFlow,
        message: t('data.address.addressError'),
        validator: addressValidator,
      },
      billingAddress: {
        required: customBillingAddress || (!!user && !customerWithCompleteBillingAddress),
        message: t('data.address.addressError'),
        validator: addressValidator,
      },
      providerChangeReason: {
        required: !user && tariffFlow,
        message: t('data.deliveryDetails.providerChangeReasonError'),
      },
      meterId: {
        required: !switchFromExistingContract && tariffFlow,
        message: t('data.deliveryDetails.meterIdError'),
        validator: meterValidator,
      },
      confirmedCancellationDate: {
        required: !switchFromExistingContract && requireCancellationAndStartDates && tariffFlow,
        message: t('data.deliveryDetails.confirmedCancellationDateError'),
      },
      terminatedEnergyProviderName: {
        required:
          !localValues.contractTerminated &&
          (Boolean(user) ||
            localValues.providerChangeReason === ProviderChangeReason.SupplierChange) &&
          !switchFromExistingContract &&
          tariffFlow,
        message: t('data.deliveryDetails.currentEnergyProviderError'),
      },
      deliveryStartDate: {
        required: !switchFromExistingContract && requireCancellationAndStartDates && tariffFlow,
        message: t('data.deliveryDetails.deliveryStartDateError'),
      },
      iban: {
        required: tariffFlow,
        message: t('data.paymentDetails.ibanError'),
        validator: ibanValidator,
      },
      accountOwner: {
        required: tariffFlow,
        message: getAccountOwnerErrorMessage(),
        validator: accountOwnerValidator,
      },
      paymentAuthorization: {
        required: tariffFlow || orderFlow === OrderFlow.batteryOptimization,
        message: t('data.paymentDetails.paymentAuthorizationError'),
      },
      termsAndConditions: {
        required: true,
        message: t('data.paymentDetails.termsAndConditionsError'),
      },
      bindingContract: {
        required:
          selectedSite?.options.createContractFlow ===
          CurrentUserSiteOptionsCreateContractFlow.SwitchFromFlatBindingContract,
        message: t('data.paymentDetails.bindingContractError'),
      },
    }),
    [
      localValues.contractTerminated,
      localValues.providerChangeReason,
      localValues.personalData,
      localValues.accountOwner,
      user,
      t,
    ],
  );

  const [validate, { errors, setErrors }] = useValidation({
    values: localValues,
    lazy: true,
    schema,
  });

  useEffect(() => {
    if (preFilledAccountOwner && !getValidAccountOwner(preFilledAccountOwner)) {
      validate(['accountOwner'], localValues);
    }
  }, []);

  const onChange = (field: string, value: string | boolean | Date | null) => {
    setLocalValues({ ...localValues, [field]: value });
  };

  const onChanges = (newValues: Partial<TFormValues>) => {
    setLocalValues({ ...localValues, ...newValues });
  };

  const onChangePersonalDataOrAddress = (field: string, values: TPersonalData | TAddress) => {
    let newValues = { ...localValues, [field]: values };

    if (field === 'personalData') {
      const accountOwner = getFullName(values as TPersonalData);
      if (accountOwner && accountOwner.length <= ACCOUNT_OWNER_MAX_LENGTH) {
        newValues = { ...newValues, accountOwner };
        validate(['accountOwner'], newValues);
      } else {
        newValues = { ...newValues, accountOwner: '' };
      }
      validate(['accountOwner'], newValues);
    }
    setLocalValues(newValues);
  };

  const onClick = () => {
    setFormValues(localValues);
    const validForm = validate(Object.keys(localValues), localValues);
    if (validForm && nextRoute) navigate(nextRoute, { state: 'data-entry' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const sections = sectionsByFlow[orderFlow!];

  return (
    <OrderFlowPage
      title={t('data.dataEntry.title')}
      className={styles.dataEntryPage}
      dataTestId="data-entry"
    >
      {customerWithCompletePersonalData ? (
        <DataCard<TPersonalData>
          fields={['companyName', 'firstName', 'lastName', 'email', 'phone']}
          i18nScope="data.personalInfo"
          dataTestId="personal-info"
          source={formValues.personalData}
        />
      ) : (
        <PersonalDataSection
          onChange={onChangePersonalDataOrAddress}
          defaultValues={formValues.personalData}
          revalidate={Boolean(errors.personalData)}
        />
      )}

      {customerWithCompleteAddress ? (
        <DataCard<TAddress>
          fields={['streetName', 'streetNumber', 'postalCode', 'city']}
          i18nScope="data.address"
          dataTestId="delivery-address"
          source={formValues.deliveryAddress}
        />
      ) : (
        <Card paddingSize="md" className={commonStyles.formSection}>
          <AddressSection
            onChange={onChangePersonalDataOrAddress}
            name="deliveryAddress"
            defaultValue={formValues.deliveryAddress}
            readonlyFields={readonlyFields}
            revalidate={Boolean(errors.deliveryAddress)}
            notification={user ? t('data.address.incompleteDataNotification') : ''}
          />
          {!user && (
            <Checkbox
              onChange={() => setCustomBillingAddress(!customBillingAddress)}
              name="customBillingAddress"
              checked={customBillingAddress}
              dataTestId="billing-address"
              className={clsx(styles.checkBox, styles.removeBottomSectionSpace)}
              clickableLabel
              aria-label={t('data.address.customBillingAddress')}
            >
              <Typography.Body2>{t('data.address.customBillingAddress')}</Typography.Body2>
            </Checkbox>
          )}
        </Card>
      )}

      {customerWithCompleteBillingAddress && (
        <DataCard<CurrentUserAccountBillingAddress>
          fields={['streetName', 'streetNumber', 'postalCode', 'city']}
          i18nScope="data.address"
          name="billingAddress"
          dataTestId="readonly-billing-address"
          source={formValues.billingAddress}
        />
      )}

      {showAddressSection && (
        <Card paddingSize="md" className={commonStyles.formSection}>
          <AddressSection
            onChange={onChangePersonalDataOrAddress}
            name="billingAddress"
            defaultValue={formValues.billingAddress}
            revalidate={Boolean(errors.billingAddress)}
            readonlyFields={readonlyFieldsBilling}
            notification={
              user && !customerWithCompleteBillingAddress
                ? t('data.address.incompleteBillingAddressNotification')
                : ''
            }
          />
        </Card>
      )}

      {!switchFromExistingContract && sections.has(DataSection.DeliveryDetails) && (
        <DeliveryDetails
          values={localValues}
          onChange={onChange}
          onChanges={onChanges}
          validate={validate}
          errors={errors}
          setErrors={setErrors}
        />
      )}

      {sections.has(DataSection.PaymentDetails) ? (
        <PaymentDetails
          values={localValues}
          onChange={onChange}
          validate={validate}
          errors={errors}
          setErrors={setErrors}
        />
      ) : (
        <Card paddingSize="md" dataTestId="checkboxes">
          <Checkboxes values={localValues} onChange={onChange} errors={errors} />
        </Card>
      )}

      <Card paddingSize="md">
        <Button
          label={t('data.continueAndReview')}
          onClick={onClick}
          dataTestId="continue-btn"
          expanded
        />
      </Card>
    </OrderFlowPage>
  );
};

export default DataEntry;
